import { useCallback, useState } from 'react';

import { ClickableOptionsCard } from 'core/Components';
import { useBreakPoints } from 'core/Hooks';
import { PagesUrl } from 'core/Utils';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  ActionIconProps,
  AspectRatio,
  Modal,
  SimpleGrid,
  Stack,
  StackProps,
  Text,
  ThemeIcon,
  Title,
} from 'ui';
import { IconEarn, IconMore, IconReceipt, IconSettings } from 'ui/Icons';

import classes from './Shortcut.module.css';

type ShortcutProps = {
  /**
   * The label to display
   */
  label?: string;
  /**
   * The URL to navigate to
   */
  to?: string;
  /**
   * The onClick handler if not navigating
   */
  onClick?: () => void;
  /**
   * The icon to display
   */
  icon: (props: { style: React.CSSProperties }) => React.ReactNode;
  ActionIconProps?: ActionIconProps;
  variant?: 'default' | 'gray';
};

const Shortcut = ({
  label,
  to,
  onClick,
  icon,
  ActionIconProps = {},
  variant = 'default',
}: ShortcutProps) => {
  return (
    <Stack gap="sm" className={classes.Shortcut} mod={{ variant }}>
      <AspectRatio ratio={1} h="auto">
        <ActionIcon
          className={classes.Shortcut__actionIcon}
          radius="md"
          size="xl"
          variant="light"
          w="100%"
          h="100%"
          component={to ? NavLink : undefined}
          to={to ?? ''}
          onClick={onClick}
          {...ActionIconProps}
        >
          <ThemeIcon
            classNames={{
              root: classes.Shortcut__themeIcon,
            }}
            size="40"
            variant="transparent"
          >
            {icon({
              style: { height: '100%', width: '100%' },
            })}
          </ThemeIcon>
        </ActionIcon>
      </AspectRatio>
      {!!label && (
        <Text fw={500} fz={12} ta="center">
          {label}
        </Text>
      )}
    </Stack>
  );
};

type ShortcutsProps = {
  /**
   * Additional props to pass to the container
   */
  containerProps?: StackProps;
};

const Shortcuts = ({ containerProps }: ShortcutsProps) => {
  const [isMoreModalOpen, setIsMoreModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isSmallScreen } = useBreakPoints();

  const handleNavigate = useCallback((to: string) => {
    setIsMoreModalOpen(false);
    navigate(to);
  }, []);

  return (
    <Stack gap={isSmallScreen ? 'md' : 'lg'} {...containerProps}>
      <Title order={2}>Shortcuts</Title>
      <SimpleGrid cols={{ base: 3, sm: 5, md: 3 }} verticalSpacing="sm">
        <Shortcut
          label="Purchases"
          icon={(props) => <IconReceipt {...props} />}
          to={PagesUrl.WALLET_PURCHASES}
        />
        <Shortcut
          label="Earnings"
          icon={(props) => <IconEarn {...props} />}
          to={PagesUrl.WALLET_EARNINGS}
        />
        <Shortcut
          label="More"
          icon={(props) => <IconMore {...props} />}
          onClick={() => setIsMoreModalOpen(true)}
        />
      </SimpleGrid>
      <Modal title="Shortcuts" opened={isMoreModalOpen} onClose={() => setIsMoreModalOpen(false)}>
        <ClickableOptionsCard>
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" c="gray.2">
                <IconReceipt />
              </ThemeIcon>
            }
            title="Purchases"
            onClick={() => handleNavigate(PagesUrl.WALLET_PURCHASES)}
          />
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" c="gray.2">
                <IconEarn />
              </ThemeIcon>
            }
            title="Earnings"
            onClick={() => handleNavigate(PagesUrl.WALLET_EARNINGS)}
          />
          <ClickableOptionsCard.Row
            leftSection={
              <ThemeIcon variant="transparent" c="gray.2">
                <IconSettings />
              </ThemeIcon>
            }
            title="Settings"
            onClick={() => handleNavigate(PagesUrl.SETTINGS)}
          />
        </ClickableOptionsCard>
      </Modal>
    </Stack>
  );
};

export { Shortcut, Shortcuts };
